"use client";

import * as React from 'react';
import { cn } from '../../lib/utils';
import { ArrowRight } from 'lucide-react';

const baseClasses = {
  light: {
    root: "rounded-full ring-1 ring-inset",
    addon: "rounded-full ring-1 ring-inset",
  },
};

const getSizeClasses = (text, icon, hasAddon) => ({
  leading: {
    md: {
      root: cn("py-1 pr-2 text-xs font-medium", hasAddon ? "pl-1" : "pl-3", !text && !icon && "pr-1"),
      addon: cn("px-2 py-0.5", text && "mr-2"),
      icon: "ml-1 size-4",
    },
    lg: {
      root: cn("py-1 pr-2 text-sm font-medium", hasAddon ? "pl-1" : "pl-3", !text && !icon && "pr-1"),
      addon: cn("px-2.5 py-0.5", text && "mr-2"),
      icon: "ml-1 size-4",
    },
  },
});

const colorClasses = {
  light: {
    brand: {
      root: "bg-blue-950 text-blue-300 ring-blue-800 hover:bg-blue-900",
      addon: "bg-gray-950 text-current ring-blue-800",
      icon: "text-blue-400",
    },
    gray: {
      root: "bg-gray-800 text-gray-300 ring-gray-700 hover:bg-gray-700",
      addon: "bg-gray-950 text-current ring-gray-700",
      icon: "text-gray-400",
    },
    error: {
      root: "bg-red-950 text-red-300 ring-red-800 hover:bg-red-900",
      addon: "bg-gray-950 text-current ring-red-800",
      icon: "text-red-400",
    },
    warning: {
      root: "bg-yellow-950 text-yellow-300 ring-yellow-800 hover:bg-yellow-900",
      addon: "bg-gray-950 text-current ring-yellow-800",
      icon: "text-yellow-400",
    },
    success: {
      root: "bg-green-950 text-green-300 ring-green-800 hover:bg-green-900",
      addon: "bg-gray-950 text-current ring-green-800",
      icon: "text-green-400",
    },
  },
};

export const BadgeGroup = React.forwardRef(({
  children,
  addonText,
  size = "md",
  color = "brand",
  theme = "light",
  align = "leading",
  className,
  iconTrailing: IconTrailing = ArrowRight,
  ...props
}, ref) => {
  const colors = colorClasses[theme][color];
  const sizes = getSizeClasses(!!children, !!IconTrailing, !!addonText)[align][size];

  const rootClasses = cn(
    "inline-flex w-max cursor-pointer items-center transition duration-100 ease-linear",
    baseClasses[theme].root,
    sizes.root,
    colors.root,
    className,
  );
  const addonClasses = cn("inline-flex items-center", baseClasses[theme].addon, sizes.addon, colors.addon);
  const iconClasses = cn(sizes.icon, colors.icon);

  return (
    <div ref={ref} className={rootClasses} {...props}>
      {/* Only render addon if there's text */}
      {addonText && (
        <span className={addonClasses}>
          {addonText}
        </span>
      )}

      {children}

      {/* Trailing icon */}
      {IconTrailing && <IconTrailing className={iconClasses} />}
    </div>
  );
});

BadgeGroup.displayName = 'BadgeGroup';
